import { Add as AddIcon, DeleteOutlined as DeleteOutlinedIcon } from '@mui/icons-material';
import Actions from '../../actions/actions';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import withDataLoaded from '../with_data_loaded';
import { subscriptionListLoader, userLoader } from '../../actions/data_loaders';
import PageSpinner from '../shared/page_spinner';
import notificationTypeMapping from './components/notification_type_mapping';
import { UserAdminDataGrid, UserAdminNoRows, UserAdminToolbar } from './components/user_admin_data_grid';
import SearchHelper from '../../helpers/search_helper';
import { Button, IconButton, Tooltip } from '@mui/material';
import FeaturePermissionsHelper from '../../helpers/feature_permissions_helper';

const deleteNotificationModal = (id, coid) => {
  Actions.requestModals({
    title: 'Are you sure you want to delete this subscription?',
    modalClass: 'user-admin-item-list-modal',
    cancelButtonText: 'Cancel',
    cancelClass: 'user-admin-delete-cancel',
    okButtonText: 'Delete',
    modalID: 'qa-notifications-unsaved-changes-modal',
    okID: 'user-admin-notification-delete-modal-confirm',
    okClass: 'action-primary user-admin-delete-confirm',
    cancelID: 'user-admin-notification-delete-cancel',
    contents: (
      <span className="qa-hidden">
        {/*this one is a hard delete for the notification*/}
        <Button
          id="user-admin-notification-hard-delete-modal-confirm"
          onClick={() => Actions.hardDeleteSubscription({ id, coid })}
          variant="contained"
          color="primary"
        >
          Yes, Delete
        </Button>
      </span>
    ),
    okProcess: async () => await Actions.deleteSubscription({ id, coid }),
  });
};

function NotificationDatatable() {
  const currUser = useSelector((state) => state.user);
  const subscriptionList = useSelector((state) => state.subscriptionList);
  const view = useSelector((state) => state.view);

  const [subscriptionsToDisplay, setSubscriptionsToDisplay] = useState(subscriptionList);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (subscriptionList?.length) {
      setSubscriptionsToDisplay(SearchHelper.search(searchText, subscriptionList, ['eventType', 'role']));
    }
  }, [subscriptionList, searchText]);

  const customToolbar = useMemo(() => {
    return UserAdminToolbar(
      'Notification List',
      true,
      <button
        id="admin-notification-list-add-button"
        className="action-primary action-alt add-item-button"
        onClick={() => {
          Actions.clearSubscription();
          Actions.setRoute(`/account/companies/${view.coid}/notifications/new`);
        }}
        disabled={!FeaturePermissionsHelper.hasPermission(currUser, 'USER_NOTIFICATION', 'CREATE')}
      >
        <AddIcon className="add-item-icon" />
        Add Notification
      </button>
    );
  }, [view?.coid, currUser]);

  if (currUser === null) {
    return <PageSpinner />;
  }

  const defaultCol = { flex: 1 };

  const columns = [
    {
      headerName: 'Type',
      field: 'eventType',
      ...defaultCol,
      renderCell: ({ row }) => notificationTypeMapping.get(row.eventType),
    },
    {
      headerName: 'Recipient',
      field: 'role',
      ...defaultCol,
      sortComparator: (a, b, param1, param2) => {
        const row1 = param1.api.getRow(param1.rowNode.id);
        const row2 = param2.api.getRow(param2.rowNode.id);

        const aRole = row1.role;
        const bRole = row2.role;
        if (aRole !== 'adhoc_subscriber' || bRole !== 'adhoc_subscriber') {
          return aRole.localeCompare(bRole);
        }
        const aEmail = row1?.additionalSubscriptionInfo?.emailAddress.toLowerCase();
        const bEmail = row1?.additionalSubscriptionInfo?.emailAddress.toLowerCase();
        return aEmail.toLowerCase().localeCompare(bEmail);
      },
      renderCell: ({ row }) => {
        if (row.role !== 'adhoc_subscriber') {
          return row.role.replaceAll('_', ' ');
        }
        return row.additionalSubscriptionInfo.emailAddress;
      },
    },
    {
      headerName: 'Actions',
      field: 'id',
      ...defaultCol,
      sortable: false,
      renderCell: ({ value }) => (
        <span id={'qa-actions-deactivate-notification'}>
          <Tooltip title={'Remove Notification'}>
            <IconButton
              aria-label="delete notification"
              onClick={(event) => {
                event.stopPropagation();
                deleteNotificationModal(value, view?.coid);
              }}
            >
              <DeleteOutlinedIcon />
            </IconButton>
          </Tooltip>
        </span>
      ),
    },
  ];

  return (
    <div className="user-admin-page datagrid-page" id="qa-notification-list-page">
      <UserAdminDataGrid
        columns={columns}
        rows={subscriptionsToDisplay}
        hideFooter
        onCellClick={({ row }) => {
          Actions.clearSubscription();
          Actions.setRoute(`/account/companies/${view.coid}/notifications/${row.id}`);
        }}
        components={{
          LoadingOverlay: PageSpinner,
          Toolbar: customToolbar,
          NoRowsOverlay: UserAdminNoRows,
        }}
        componentsProps={{
          toolbar: {
            value: searchText,
            clearSearch: () => setSearchText(''),
            onChange: (event) => setSearchText(event.target.value),
          },
        }}
        rowClassFunc={(row) => {
          const eventText = notificationTypeMapping.get(row?.eventType);
          const roleText = row.role === 'adhoc_subscriber' ? row.additionalSubscriptionInfo.emailAddress : row.role;
          return `qa-notif-list-row-${eventText}-${roleText}`?.replace(/\s|@|\.|_/g, '').toLowerCase();
        }}
      />
    </div>
  );
}
const withNotificationLoaded = withDataLoaded({
  WithData: NotificationDatatable,
  WithoutData: PageSpinner,
  data: [userLoader, subscriptionListLoader],
});

export default withNotificationLoaded;
